// DataProvider.tsx
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import { find } from "lodash";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CancellableAPIRequest } from "restAPI";
import FindingAPI from "restAPI/FindingAPI";
import { FindingStats } from "types/api/findingStats";
import { FilterInfo } from "types/pagination";

type DataCache = {
  status: "idle" | "loading" | "success" | "error";
  data?: FindingStats;
  error?: boolean;
  promise?: Promise<void>;
  key: string;
};

type FetchData = (filters: FilterInfo) => void;

type ContextType = {
  fetchData: FetchData;
  useData: (filters: FilterInfo) => DataCache | undefined;
};
type CancellType = CancellableAPIRequest<FindingStats>["cancel"];

const getKey = (filters: FilterInfo) => JSON.stringify(filters);

const DataContext = createContext<ContextType>({
  fetchData: () => void 0,
  useData: () => ({ status: "error", key: "unknown" }),
});

export function FindingBreakdownProvider({
  children,
  filters,
  groupBy,
}: {
  children: ReactNode;
  filters?: FilterInfo;
  groupBy?: string[];
}) {
  const { findingBreakdownGroupByField } = useGlobalSettingsContext();
  const cancellRefs = useRef<Record<string, CancellType>>({});
  const [stats, setStats] = useState<DataCache[]>([]);

  const fetchData = useCallback<FetchData>((filters) => {
    const key = getKey(filters);
    setStats((prev) => {
      const prevStats = find(prev, { key });
      if (!prevStats) {
        const cache: DataCache = {
          status: "idle",
          key,
        };
        return [...prev, cache];
      }
      return prev;
    });
  }, []);

  const idleRequests = useMemo(() => {
    return stats.filter((stat) => stat.status === "idle");
  }, [stats]);

  const useData = useCallback(
    (filters: FilterInfo) => {
      return stats.find((stat) => stat.key === getKey(filters));
    },
    [stats],
  );

  useEffect(() => {
    const cancellables = cancellRefs.current;
    return () => {
      setStats([]);
      Object.values(cancellables).forEach((c) => c());
    };
  }, [filters, groupBy]);

  useEffect(() => {
    // const cancellables = [];
    idleRequests.forEach(({ key }) => {
      setStats((prev) => {
        const statsObject = prev.find((statsObject) => statsObject.key === key);
        if (statsObject) {
          statsObject.status = "loading";
          return [...prev];
        }
        return prev;
      });
      const filters = JSON.parse(key) as FilterInfo;
      const { cancel, promise } = FindingAPI.getStatsAll({
        filters: filters,
        aggField: "businessUnit",
        secAggField: findingBreakdownGroupByField,
      });

      cancellRefs.current[key] = cancel;

      promise.then((response: FindingStats) => {
        setStats((prev) => {
          const statsObject = prev.find(
            (statsObject) => statsObject.key === key,
          );
          if (statsObject) {
            statsObject.status = "success";
            statsObject.data = response;
            return [...prev];
          }
          return prev;
        });
        delete cancellRefs.current[key];
      });
    });
  }, [idleRequests, findingBreakdownGroupByField]);

  const value = useMemo<ContextType>(() => {
    return {
      fetchData,
      useData,
    };
  }, [fetchData, useData]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useFindingBreakdownProvider() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useDataContext must be inside <DataProvider>");
  return ctx;
}
